<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Payment Gateways</h5>
          <p class="page-sub">Configure payment providers and their API keys</p>
        </div>
  
        <div class="page-actions">
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search gateways..."
              v-model="tableData.search"
              @input="tableData.page = 1; fetchData()"
            />
          </div>
  
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="[0,1]">All Statuses</option>
            <option :value="[1]">Active</option>
            <option :value="[0]">Inactive</option>
          </select>
        </div>
      </div>
  
      <!-- Data card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading payment gateways...</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="credit-card" /></div>
          <p class="state-title">No payment gateways found</p>
          <p class="state-sub">Payment gateways will appear here once configured.</p>
        </div>
  
        <template v-else>
  
          <!-- Toolbar -->
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
              of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <!-- Table -->
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Gateway</th>
                  <th>Public Key</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in details" :key="item.id">
  
                  <td class="text-muted-cell">{{ pagination.from + index }}</td>
  
                  <!-- Gateway name + logo -->
                  <td>
                    <div class="gateway-cell">
                      <div class="gateway-logo-wrap">
                        <img
                          v-if="item.logo"
                          :src="item.logo"
                          :alt="item.name"
                          class="gateway-logo"
                        />
                        <div v-else class="gateway-logo-placeholder">
                          <font-awesome-icon icon="credit-card" />
                        </div>
                      </div>
                      <span class="gateway-name">{{ item.name }}</span>
                    </div>
                  </td>
  
                  <!-- Masked public key -->
                  <td>
                    <span v-if="item.public_key" class="key-badge">
                      {{ maskKey(item.public_key) }}
                    </span>
                    <span v-else class="text-muted-cell">Not configured</span>
                  </td>
  
                  <!-- Toggle -->
                  <td>
                    <label class="toggle-switch">
                      <input
                        type="checkbox"
                        :true-value="1"
                        :false-value="0"
                        v-model="statuses[index]"
                        @change="updateStatus(item.id)"
                      />
                      <span class="toggle-track"><span class="toggle-thumb"></span></span>
                      <span class="toggle-label" :class="statuses[index] ? 'on' : 'off'">
                        {{ statuses[index] ? 'Active' : 'Inactive' }}
                      </span>
                    </label>
                  </td>
  
                  <!-- Actions -->
                  <td class="col-actions">
                    <div class="actions-cell">
                      <button
                        class="action-btn action-btn--edit"
                        title="Edit"
                        @click="openEdit(item)"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Pagination -->
          <div class="table-footer">
            <pagination
              :pagination="pagination"
              @prev="tableData.page = pagination.currentPage - 1; fetchData()"
              @next="tableData.page = pagination.currentPage + 1; fetchData()"
            />
          </div>
  
        </template>
      </div>
  
      <!-- ── Edit Modal ── -->
      <div class="modal-overlay" :class="{ open: editOpen }" @click.self="closeEdit">
        <div class="modal-box">
          <div class="modal-header-styled">
            <div class="modal-title-group">
              <span class="modal-title-styled">Edit Payment Gateway</span>
              <span class="modal-title-sub">{{ edit_name }}</span>
            </div>
            <button class="modal-close-btn" @click="closeEdit">
              <font-awesome-icon icon="times" />
            </button>
          </div>
  
          <form @submit.prevent="updateGateway">
            <div class="modal-body-styled">
  
              <div class="form-group-styled">
                <label class="form-label-styled">Gateway Name</label>
                <input
                  v-model="edit_name"
                  type="text"
                  class="form-input-styled form-input--readonly"
                  readonly
                />
              </div>
  
              <div class="form-group-styled">
                <label class="form-label-styled">
                  Public Key
                  <span class="key-hint">
                    <font-awesome-icon icon="info-circle" /> Used for client-side requests
                  </span>
                </label>
                <div class="key-input-wrap">
                  <input
                    v-model="edit_public_key"
                    :type="showPublicKey ? 'text' : 'password'"
                    class="form-input-styled"
                    placeholder="Enter public key" required=""
                  />
                  <button type="button" class="key-toggle" @click="showPublicKey = !showPublicKey">
                    <font-awesome-icon :icon="showPublicKey ? 'eye-slash' : 'eye'" />
                  </button>
                </div>
              </div>
  
              <div class="form-group-styled" style="margin-bottom:0;">
                <label class="form-label-styled">
                  Secret Key
                  <span class="key-hint">
                    <font-awesome-icon icon="info-circle" /> Keep this confidential
                  </span>
                </label>
                <div class="key-input-wrap">
                  <input
                    v-model="edit_secret_key"
                    :type="showSecretKey ? 'text' : 'password'"
                    class="form-input-styled"
                    placeholder="Enter secret key" required=""
                  />
                  <button type="button" class="key-toggle" @click="showSecretKey = !showSecretKey">
                    <font-awesome-icon :icon="showSecretKey ? 'eye-slash' : 'eye'" />
                  </button>
                </div>
              </div>
  
            </div>
  
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeEdit">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="crudLoading">
                <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- Backdrop -->
      <div v-if="editOpen" class="modal-backdrop-custom" />
  
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import AdminHeader from "./administrationheadertab.vue";
  
  export default {
    name: 'PaymentGateway',
    components: { AdminHeader },
  
    data() {
      return {
        details:  [],
        statuses: [],
        loading:     false,
        crudLoading: false,
        editOpen:    false,
        showPublicKey: false,
        showSecretKey: false,
  
        gateway_id:      '',
        edit_name:       '',
        edit_public_key: '',
        edit_secret_key: '',
  
        tableData: { page: 1,
          draw:   0,
          length: 5,
          search: '',
          column: 0,
          dir:    'desc',
          status: [0, 1],
        },
  
        pagination: {
          lastPage: '', currentPage: '', total: '',
          lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
          from: 1, to: '',
        },
  
        toast: useToast(),
      };
    },
  
    methods: {
      // ── Helpers ──────────────────────────────────────────────────────
      maskKey(key) {
        if (!key || key.length <= 8) return key;
        return key.substring(0, 6) + '•••••••••••••' + key.slice(-4);
      },
  
      // ── Modal ────────────────────────────────────────────────────────
      openEdit(item) {
        this.gateway_id      = item.id;
        this.edit_name       = item.name;
        this.edit_public_key = item.public_key;
        this.edit_secret_key = item.secret_key;
        this.showPublicKey   = false;
        this.showSecretKey   = false;
        this.editOpen        = true;
      },
  
      closeEdit() {
        this.editOpen        = false;
        this.edit_public_key = '';
        this.edit_secret_key = '';
      },
  
      // ── Data ─────────────────────────────────────────────────────────
      async fetchData(url = 'settings/payment-gateway/all') {
        this.loading = true;
        this.tableData.draw++;
        try {
          const { data } = await axios.get(url, { params: this.tableData });
          this.details  = data.data.data;
          this.statuses = this.details.map(d => d.status);
          this.configPagination(data.data);
        } catch (err) {
          this.toast.error(err.response?.data?.message ?? 'Could not load payment gateways.');
        } finally {
          this.loading = false;
        }
      },
  
      async updateStatus(id) {
        try {
          const { data } = await axios.put('settings/payment-gateway/update-status/' + id);
          this.toast.success(data.message);
          this.fetchData();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) this.toast.warning('Invalid request.');
          else this.toast.error('Could not update status.');
        }
      },
  
      async updateGateway() {
        this.crudLoading = true;
        try {
          await axios.put('/settings/payment-gateway/update', {
            id:         this.gateway_id,
            name:       this.edit_name,
            public_key: this.edit_public_key,
            secret_key: this.edit_secret_key,
          });
          this.toast.success('Payment gateway updated successfully.');
          this.closeEdit();
          this.fetchData();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) this.toast.warning('Invalid input. Please check and try again.');
          else if (status === 400) this.toast.error('This gateway cannot be edited.');
          else this.toast.error('An error occurred. Please try again.');
        } finally {
          this.crudLoading = false;
        }
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  
    mounted() { this.fetchData(); },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search & Filter ─────────────────────────────────────────────────── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:220px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
  .state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell  { color:#4a5e78; }
  .mt-3 { margin-top:12px; }
  
  /* ── Table toolbar ───────────────────────────────────────────────────── */
  .table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count    { font-size:13px; color:#7a90b0; }
  .table-count strong { color:#1a2b45; }
  .toolbar-right  { display:flex; align-items:center; gap:8px; }
  .rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
  .col-actions { width:60px; text-align:center; }
  
  /* ── Gateway cell ────────────────────────────────────────────────────── */
  .gateway-cell         { display:flex; align-items:center; gap:12px; }
  .gateway-logo-wrap    { width:80px; height:36px; border-radius:7px; border:1px solid #e4eaf2; background:#f8fafc; display:flex; align-items:center; justify-content:center; flex-shrink:0; padding:4px 8px; }
  .gateway-logo         { max-width:100%; max-height:100%; object-fit:contain; }
  .gateway-logo-placeholder { color:#7a90b0; font-size:16px; }
  .gateway-name         { font-size:13px; font-weight:600; color:#1a2b45; }
  
  /* ── Key badge ───────────────────────────────────────────────────────── */
  .key-badge { font-family:monospace; font-size:12px; color:#3d5068; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; letter-spacing:.04em; }
  
  /* ── Toggle switch ───────────────────────────────────────────────────── */
  .toggle-switch { display:inline-flex; align-items:center; gap:8px; cursor:pointer; user-select:none; }
  .toggle-switch input { display:none; }
  .toggle-track  { position:relative; width:36px; height:20px; border-radius:20px; background:#d1d9e8; transition:background .2s; flex-shrink:0; }
  .toggle-switch input:checked + .toggle-track { background:#16a34a; }
  .toggle-thumb  { position:absolute; top:3px; left:3px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
  .toggle-switch input:checked + .toggle-track .toggle-thumb { transform:translateX(16px); }
  .toggle-label  { font-size:12px; font-weight:600; }
  .toggle-label.on  { color:#16a34a; }
  .toggle-label.off { color:#7a90b0; }
  
  /* ── Action buttons ──────────────────────────────────────────────────── */
  .actions-cell  { display:flex; align-items:center; justify-content:center; }
  .action-btn    { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
  .action-btn--edit:hover { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
  
  /* ── Table footer ────────────────────────────────────────────────────── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Modal ───────────────────────────────────────────────────────────── */
  .modal-overlay         { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
  .modal-overlay.open    { opacity:1; pointer-events:all; }
  .modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
  .modal-box             { background:#fff; border-radius:14px; width:100%; max-width:480px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
  .modal-overlay.open .modal-box { transform:translateY(0); }
  
  .modal-header-styled   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
  .modal-title-group     { display:flex; flex-direction:column; gap:3px; }
  .modal-title-styled    { font-size:16px; font-weight:700; color:#1a2b45; }
  .modal-title-sub       { font-size:12px; color:#7a90b0; font-weight:500; }
  .modal-close-btn       { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
  .modal-close-btn:hover { background:#f4f7fb; }
  .modal-body-styled     { padding:20px 24px; display:flex; flex-direction:column; gap:16px; }
  .modal-footer-styled   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }
  
  /* ── Form elements ───────────────────────────────────────────────────── */
  .form-group-styled      { display:flex; flex-direction:column; gap:6px; }
  .form-label-styled      { font-size:13px; font-weight:600; color:#1a2b45; display:flex; align-items:center; justify-content:space-between; }
  .key-hint               { font-size:11px; font-weight:400; color:#7a90b0; display:flex; align-items:center; gap:4px; }
  .form-input-styled      { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:monospace; }
  .form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .form-input--readonly   { background:#f8fafc; color:#7a90b0; cursor:not-allowed; font-family:inherit; }
  .key-input-wrap         { position:relative; display:flex; align-items:center; }
  .key-input-wrap .form-input-styled { padding-right:40px; }
  .key-toggle             { position:absolute; right:10px; border:none; background:transparent; color:#7a90b0; cursor:pointer; font-size:14px; padding:0; transition:color .15s; }
  .key-toggle:hover       { color:#1a2b45; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.55; cursor:not-allowed; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; }
  .me-1 { margin-right:4px; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .page-header, .page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
  }
  </style>